import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send,
  Sparkles,
  Loader2,
  Globe,
  Bot,
  User,
  Workflow,
  Database,
  Layers,
  Clipboard,
  Cpu,
  MessageSquare,
  ChevronDown
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SPECIALTIES = [
  {
    id: "general",
    name: "General Consultant",
    role: "General automation consultant",
    badge: "General",
    icon: Sparkles,
    color: "#00F0FF",
    desc: "A versatile advisor for workflow architecture, system design, and overall business operations automation.",
    samples: [
      "Map a typical customer onboarding automation flow",
      "Recommend an AI tech stack for a growing SaaS startup",
      "How to replace busywork in a marketing team with AI",
      "Explain n8n vs Make.com for business automation"
    ]
  },
  {
    id: "crm",
    name: "CRM Automator",
    role: "CRM sync & lead router specialist",
    badge: "CRM & Leads",
    icon: Workflow,
    color: "#00FF66",
    desc: "Deep integration expert for HubSpot, Salesforce, active campaigns, lead routing, scoring, and automated cleanup.",
    samples: [
      "Automate inbound lead routing to Slack and Salesforce",
      "How to sync HubSpot contacts to Google Sheets bi-directionally",
      "Set up lead scoring using OpenAI and database lookups",
      "Best way to clean up duplicate leads in HubSpot automatically"
    ]
  },
  {
    id: "document",
    name: "DocuParser OCR",
    role: "Invoicing & Document AI agent",
    badge: "Documents",
    icon: Database,
    color: "#DFFF00",
    desc: "Extract text and structured JSON from PDF invoices, contracts, receipts, and bank statements automatically.",
    samples: [
      "Extract line items from invoice PDFs and save to QuickBooks",
      "Automate reading email attachments (receipts) and pushing to Google Drive",
      "Create a Python script to parse lease contracts with OCR",
      "Design an n8n flow to extract data from vendor receipts"
    ]
  },
  {
    id: "support",
    name: "Support Copilot",
    role: "24/7 support & ticket agent",
    badge: "Customer Support",
    icon: Bot,
    color: "#FF3366",
    desc: "Build self-improving support bots, hook up Zendesk/Intercom triggers, and create intelligent human escalation filters.",
    samples: [
      "Design a human-in-the-loop support handoff rule",
      "Build a Slack support bot connected to a Notion knowledge base",
      "How to automatically tag incoming support tickets by urgency",
      "Integrate Twilio voice bot with live Zendesk agent escalation"
    ]
  },
  {
    id: "growth",
    name: "Growth Engine",
    role: "Lead enrichment & outreach specialist",
    badge: "Growth",
    icon: Layers,
    color: "#BC13FE",
    desc: "Scale outreach systems, run automated database enrichment, create customized cold emails, and secure high deliverability.",
    samples: [
      "Scrape search results, enrich contacts via Apollo, and write cold email draft",
      "Set up an automated LinkedIn outbound outreach sequence",
      "Build a lead enrichment flow using Clay and ChatGPT",
      "Maximize email deliverability for a cold outbound domain stack"
    ]
  }
];

export const AgentChat = ({ onOpenLead }) => {
  const navigate = useNavigate();
  const [specialty, setSpecialty] = useState(SPECIALTIES[0]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  const scrollRef = useRef(null);
  const stepsTimerRef = useRef(null);

  const thoughtSteps = [
    "Analyzing query intent & parameters...",
    "Querying DuckDuckGo live web search index...",
    "Filtering & ranking search results...",
    "Synthesizing answer using Gemini model..."
  ];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, stepIndex]);

  useEffect(() => {
    if (loading) {
      setStepIndex(0);
      let step = 0;
      stepsTimerRef.current = setInterval(() => {
        if (step < thoughtSteps.length - 1) {
          step += 1;
          setStepIndex(step);
        }
      }, 1200);
    } else {
      if (stepsTimerRef.current) {
        clearInterval(stepsTimerRef.current);
      }
    }
    return () => {
      if (stepsTimerRef.current) {
        clearInterval(stepsTimerRef.current);
      }
    };
  }, [loading]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || loading) return;

    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);

    try {
      const { data } = await api.post("/agent/chat", {
        prompt: text,
        session_id: sessionId,
        history: messages.slice(-6),
        specialty: specialty.id
      });
      setSessionId(data.session_id);
      setMessages([
        ...next,
        {
          role: "assistant",
          content: data.answer,
          sources: data.sources || [],
          model: data.model,
          specialtyId: specialty.id
        }
      ]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Agent failed. Try again.";
      toast.error(typeof msg === "string" ? msg : "Agent failure");
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Safe inline markdown element generator
  const parseInlineCode = (text) => {
    const codeParts = text.split(/(`.*?`)/g);
    return codeParts.map((cp, cIdx) => {
      if (cp.startsWith("`") && cp.endsWith("`")) {
        return (
          <code key={cIdx} className="px-1 py-0.5 rounded bg-white/10 font-mono text-[11px] text-cyan-400">
            {cp.slice(1, -1)}
          </code>
        );
      }
      return cp;
    });
  };

  const parseInline = (text) => {
    const boldParts = text.split(/(\*\*.*?\*\*)/g);
    return boldParts.map((bp, bIdx) => {
      if (bp.startsWith("**") && bp.endsWith("**")) {
        const inner = bp.slice(2, -2);
        return (
          <strong key={bIdx} className="font-bold text-white">
            {parseInlineCode(inner)}
          </strong>
        );
      }
      return parseInlineCode(bp);
    });
  };

  const renderMarkdown = (text) => {
    if (!text) return "";
    const parts = text.split(/(```[\s\S]*?```)/g);
    return parts.map((part, idx) => {
      if (part.startsWith("```")) {
        const match = part.match(/```(\w*)\n([\s\S]*?)```/);
        const lang = match ? match[1] : "";
        const code = match ? match[2] : part.slice(3, -3);
        return (
          <div key={idx} className="my-3 rounded-xl overflow-hidden border border-white/10 bg-black/60 font-mono text-xs">
            <div className="flex items-center justify-between px-3.5 py-1.5 border-b border-white/10 bg-white/5 text-zinc-400">
              <span>{lang || "code"}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(code.trim());
                  toast.success("Code copied to clipboard!");
                }}
                className="hover:text-white transition-colors flex items-center gap-1 py-0.5 px-2 rounded hover:bg-white/5"
              >
                <Clipboard className="w-3 h-3" />
                <span>Copy</span>
              </button>
            </div>
            <pre className="p-3 overflow-x-auto text-zinc-100 whitespace-pre">
              {code.trim()}
            </pre>
          </div>
        );
      } else {
        const lines = part.split("\n");
        return lines.map((line, lIdx) => {
          let content = line;
          if (content.startsWith("### ")) {
            return (
              <h4 key={`${idx}-${lIdx}`} className="text-sm font-bold text-white mt-3 mb-1.5">
                {parseInline(content.slice(4))}
              </h4>
            );
          } else if (content.startsWith("## ")) {
            return (
              <h3 key={`${idx}-${lIdx}`} className="text-base font-bold text-white mt-4 mb-2">
                {parseInline(content.slice(3))}
              </h3>
            );
          } else if (content.startsWith("# ")) {
            return (
              <h2 key={`${idx}-${lIdx}`} className="text-lg font-black text-white mt-5 mb-2.5">
                {parseInline(content.slice(2))}
              </h2>
            );
          }
          if (content.startsWith("- ") || content.startsWith("* ")) {
            return (
              <ul key={`${idx}-${lIdx}`} className="list-disc pl-4 my-0.5 space-y-0.5 text-zinc-300">
                <li>{parseInline(content.slice(2))}</li>
              </ul>
            );
          }
          const numMatch = content.match(/^(\d+)\.\s(.*)/);
          if (numMatch) {
            return (
              <ol key={`${idx}-${lIdx}`} className="list-decimal pl-4 my-0.5 space-y-0.5 text-zinc-300" start={parseInt(numMatch[1])}>
                <li>{parseInline(numMatch[2])}</li>
              </ol>
            );
          }
          if (content.trim() === "") {
            return <div key={`${idx}-${lIdx}`} className="h-1.5" />;
          }
          return (
            <p key={`${idx}-${lIdx}`} className="my-1 text-zinc-300 leading-relaxed text-[13px]">
              {parseInline(content)}
            </p>
          );
        });
      }
    });
  };

  const handleActionClick = (actionType, content, spec) => {
    if (actionType === "copy") {
      navigator.clipboard.writeText(content);
      toast.success("Solution copied to clipboard!");
    } else if (actionType === "configure") {
      localStorage.setItem("pending_needs", `Specialty: ${spec.name}\nProposed Workflow Summary:\n${content}`);
      onOpenLead?.();
    } else if (actionType === "consult") {
      navigate("/consult", {
        state: {
          needs: `Discussing solution for ${spec.name} agent response:\n\n${content.slice(0, 300)}...`
        }
      });
    }
  };

  const ActiveIconComponent = specialty.icon;

  return (
    <div className="relative w-full max-w-3xl mx-auto" data-testid="agent-chat">
      <div className="glass-strong rounded-3xl overflow-hidden shadow-[0_0_60px_rgba(188,19,254,0.15)] border border-white/10">
        
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 bg-black/25">
          <div className="flex items-center gap-3">
            {/* Specialty Icon/Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 transition-colors text-left"
              >
                <ActiveIconComponent className="w-4 h-4" style={{ color: specialty.color }} />
                <span className="text-[11px] font-bold text-white tracking-wider uppercase">
                  {specialty.name}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
              </button>
              
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1.5 w-60 bg-zinc-950 border border-white/10 rounded-2xl p-2 z-[60] shadow-2xl">
                  {SPECIALTIES.map((spec) => {
                    const SpecIcon = spec.icon;
                    return (
                      <button
                        key={spec.id}
                        onClick={() => {
                          setSpecialty(spec);
                          setSessionId(null);
                          setMessages([]);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left p-2.5 rounded-xl flex items-center gap-2.5 transition-colors ${
                          specialty.id === spec.id
                            ? "bg-white/[0.06] text-white"
                            : "hover:bg-white/[0.03] text-zinc-400 hover:text-white"
                        }`}
                      >
                        <SpecIcon className="w-4 h-4" style={{ color: spec.color }} />
                        <span className="text-xs font-bold">{spec.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <p className="text-[10px] text-zinc-500 font-mono">
                gemini · grounded search
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 text-[11px] text-zinc-400 bg-white/5 px-2.5 py-1 rounded-full border border-white/5">
            <Globe className="w-3.5 h-3.5 text-cyan-400" />
            <span>live web search</span>
          </div>
        </div>

        {/* Body / Chat Feed */}
        <div
          ref={scrollRef}
          className="px-5 py-5 space-y-4 max-h-[380px] min-h-[180px] overflow-y-auto bg-[#020205]/20"
          data-testid="agent-chat-body"
        >
          {messages.length === 0 && (
            <div className="text-center py-6 max-w-lg mx-auto">
              <p className="text-zinc-400 text-sm leading-relaxed">
                Describe the workflow or task you want automated. This specialized agent will research solutions online and outline a setup guide.
              </p>
              
              <div className="flex flex-wrap justify-center gap-1.5 mt-5">
                {specialty.samples.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`agent-sample-${s.slice(0, 14).replace(/\s/g, "-").toLowerCase()}`}
                    className="text-[11px] px-3 py-1.5 rounded-full border border-white/5 bg-white/[0.02] text-zinc-300 hover:border-cyan-400/40 hover:text-cyan-400 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => {
            const isUser = m.role === "user";
            const currentSpec = SPECIALTIES.find((s) => s.id === m.specialtyId) || specialty;
            return (
              <div
                key={i}
                data-testid={`agent-msg-${i}`}
                className={`flex gap-3 ${isUser ? "flex-row-reverse" : "flex-row animate-fade-up"}`}
              >
                {/* Icon avatar */}
                <div
                  className={`shrink-0 w-7.5 h-7.5 rounded-xl border flex items-center justify-center ${
                    isUser
                      ? "border-white/15 bg-white/5"
                      : "border-cyan-400/30"
                  }`}
                  style={{
                    backgroundColor: isUser ? "" : `${currentSpec.color}15`,
                    borderColor: isUser ? "" : `${currentSpec.color}35`
                  }}
                >
                  {isUser ? (
                    <User className="w-3.5 h-3.5 text-zinc-300" />
                  ) : (
                    (() => {
                      const SpecIcon = currentSpec.icon;
                      return <SpecIcon className="w-3.5 h-3.5" style={{ color: currentSpec.color }} />;
                    })()
                  )}
                </div>

                {/* Bubble content */}
                <div className="max-w-[85%] flex flex-col gap-1">
                  <div
                    className={`rounded-2xl px-4 py-3 text-sm leading-relaxed border ${
                      isUser
                        ? "bg-white text-black border-white"
                        : "bg-white/[0.03] border-white/10 text-zinc-100"
                    }`}
                  >
                    {isUser ? m.content : renderMarkdown(m.content)}

                    {/* Grounding references */}
                    {!isUser && m.sources?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold mb-1.5">
                          Sources Cited
                        </p>
                        <ul className="space-y-1">
                          {m.sources.slice(0, 3).map((s, sIdx) => (
                            <li key={sIdx} className="text-[11px] text-zinc-400">
                              <span className="text-cyan-400 font-mono">[{sIdx + 1}]</span>{" "}
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noreferrer"
                                className="hover:text-cyan-400 transition-colors underline-offset-2 hover:underline inline-block max-w-[90%] truncate"
                              >
                                {s.title || s.url}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Bubble Actions */}
                  {!isUser && (
                    <div className="flex items-center gap-2 mt-1.5 pl-1">
                      <button
                        onClick={() => handleActionClick("configure", m.content, currentSpec)}
                        className="text-[10px] font-bold text-white hover:text-black hover:bg-cyan-400 border border-white/15 bg-white/5 rounded-md px-2.5 py-1 flex items-center gap-1 transition-all"
                      >
                        <Cpu className="w-2.5 h-2.5" />
                        <span>Deploy Flow</span>
                      </button>
                      <button
                        onClick={() => handleActionClick("consult", m.content, currentSpec)}
                        className="text-[10px] font-bold text-zinc-400 hover:text-white border border-white/10 bg-transparent rounded-md px-2.5 py-1 flex items-center gap-1 hover:bg-white/5 transition-all"
                      >
                        <MessageSquare className="w-2.5 h-2.5" />
                        <span>Consult Engineer</span>
                      </button>
                      <button
                        onClick={() => handleActionClick("copy", m.content, currentSpec)}
                        className="text-[10px] text-zinc-500 hover:text-zinc-300 px-1 py-1"
                      >
                        Copy
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Loading steps stepper */}
          {loading && (
            <div className="flex gap-3">
              <div
                className="shrink-0 w-7.5 h-7.5 rounded-xl border flex items-center justify-center"
                style={{
                  backgroundColor: `${specialty.color}15`,
                  borderColor: `${specialty.color}35`
                }}
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: specialty.color }} />
              </div>
              <div className="max-w-[85%] bg-white/[0.02] border border-white/5 rounded-2xl px-4 py-3 min-w-[240px]">
                <div className="space-y-2">
                  {thoughtSteps.map((step, idx) => {
                    const isActive = stepIndex === idx;
                    const isCompleted = stepIndex > idx;
                    return (
                      <div key={idx} className="flex items-center gap-2 text-[11px]">
                        {isCompleted ? (
                          <span className="w-3.5 h-3.5 rounded-full bg-cyan-400/20 flex items-center justify-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                          </span>
                        ) : isActive ? (
                          <Loader2 className="w-3 h-3 animate-spin text-cyan-400 shrink-0" />
                        ) : (
                          <span className="w-3 h-3 rounded-full border border-white/10 shrink-0" />
                        )}
                        <span
                          className={
                            isCompleted
                              ? "text-zinc-500 line-through"
                              : isActive
                              ? "text-white font-bold"
                              : "text-zinc-600"
                          }
                        >
                          {step}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input composer */}
        <div className="px-4 py-3 border-t border-white/5 bg-black/40">
          <div className="flex items-end gap-2">
            <textarea
              data-testid="agent-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder={`Ask: 'How do I automate Stripe → Slack alerts?'`}
              className="flex-1 bg-transparent border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none resize-none max-h-32"
            />
            <button
              data-testid="agent-send-btn"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="h-11 px-4 rounded-xl text-black font-bold text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors hover:scale-[1.02]"
              style={{
                backgroundColor: specialty.color
              }}
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
          
          <div className="flex items-center justify-between mt-2 text-[9px] uppercase tracking-wider text-zinc-600 font-mono">
            <span>Powered by Google Gemini</span>
            <span>Grounded search</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AgentChat;
